import { useState, useCallback } from 'react';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import { getHandler } from './payments/paymentHandlerRegistry';
import './payments/handlers';

/**
 * usePaymentHandler hook.
 * Orchestrate payment routing by resolving the correct handler from the registry
 * and executing it with the appropriate context.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object.
 * @param {object} params.env - Environment configuration.
 * @param {object} params.purchaser - Purchaser data.
 * @param {object} params.actions - Action callbacks from useQuickCheckoutActions.
 * @param {object} params.engineHooks - Engine-specific hooks (e.g., evertecPayment).
 * @returns {object} handlePayment function and isProcessing state.
 */
function usePaymentHandler({ purchase, env, purchaser, actions, engineHooks }) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayment = useCallback(async () => {
    const { selectedPaymentOption, selectedPaymentMethod, paymentEngine, isUpdating } = purchase;

    if (isUpdating || isProcessing) return;

    actions.clearError();

    const paymentType = selectedPaymentOption || 'credit_card';

    const context = {
      selectedPaymentMethod,
      paymentEngine,
      env,
    };

    const handler = getHandler(paymentType, context);

    if (!handler) {
      errorMonitoring.notify({
        error: new Error(`No payment handler registered for: ${paymentType}`),
        info: { from: 'usePaymentHandler', paymentType, paymentEngine, token: purchase.token },
      });
      return;
    }

    setIsProcessing(true);
    try {
      await handler.execute({
        purchase,
        env,
        purchaser,
        engineHooks,
        ...actions,
      });
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: { from: 'usePaymentHandler:execute', paymentType, token: purchase.token },
      });
    } finally {
      setIsProcessing(false);
    }
  }, [purchase, env, purchaser, actions, engineHooks, isProcessing]);

  return {
    handlePayment,
    isProcessing,
  };
}

export default usePaymentHandler;
